import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Globe, Mail, Twitter, Linkedin, Instagram } from 'lucide-react';

import { safeFetch } from '../../config/api';

export const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    // Always fetch fresh settings (don't use stale cache for social links)
    const fetchSettings = async () => {
      try {
        const response = await safeFetch(`/api/company-settings`);
        if (response.ok) {
          const data = await response.json();
          setSettings(data);
          // Update sessionStorage with fresh data
          sessionStorage.setItem('companySettings', JSON.stringify(data));
          console.log('[Footer] Company settings loaded:', {
            twitter_url: data.twitter_url || '(empty)',
            linkedin_url: data.linkedin_url || '(empty)',
            instagram_url: data.instagram_url || '(empty)',
            contact_email: data.contact_email || '(empty)'
          });
        }
      } catch (error) {
        console.error('Error fetching company settings:', error);
        // Try cached version as fallback
        const cached = sessionStorage.getItem('companySettings');
        if (cached) {
          setSettings(JSON.parse(cached));
        }
      }
    };
    fetchSettings();
  }, []);

  const footerLinks = {
    'Destinations': [
      { name: 'All Countries', href: '/countries' },
      { name: 'All Cities', href: '/cities' },
      { name: 'Dubai', href: '/cities/dubai' },
      { name: 'Bangkok', href: '/cities/bangkok' },
      { name: 'Lisbon', href: '/cities/lisbon' },
      { name: 'Barcelona', href: '/cities/barcelona' },
      { name: 'Bali', href: '/cities/bali' },
    ],
    'Tools': [
      { name: 'Compare Countries', href: '/tools/compare' },
      { name: 'Compare Cities', href: '/tools/city-comparison' },
      { name: 'Cost Calculator', href: '/tools/calculator' },
      { name: 'Salary Calculator', href: '/tools/salary-needed-calculator' },
      { name: 'Rent Calculator', href: '/tools/rent-affordability-calculator' },
      { name: 'Visa Checker', href: '/tools/digital-nomad-visa-checker' },
    ],
    'Resources': [
      { name: 'Blog', href: '/blog' },
      { name: 'Moving Guides', href: '/guides' },
      { name: 'Data Sources & Methodology', href: '/data-sources-methodology' },
    ],
    'Company': [
      { name: 'About Us', href: '/about' },
      { name: 'Contact', href: '/contact' },
      { name: 'Privacy Policy', href: '/privacy' },
      { name: 'Terms of Service', href: '/terms' },
      { name: 'Sitemap', href: '/sitemap' },
    ],
  };

  // Helper function to validate URL
  const isValidUrl = (url) => {
    if (!url || typeof url !== 'string') return false;
    const trimmed = url.trim();
    return trimmed.length > 0 && (trimmed.startsWith('https://') || trimmed.startsWith('http://'));
  };

  // Social media icons with conditional rendering - only show if URL exists and is valid
  // Order: Twitter, LinkedIn, Instagram, Email
  const socialLinks = [
    { 
      url: settings?.twitter_url, 
      icon: Twitter, 
      label: 'Twitter',
      isValid: isValidUrl(settings?.twitter_url)
    },
    { 
      url: settings?.linkedin_url, 
      icon: Linkedin, 
      label: 'LinkedIn',
      isValid: isValidUrl(settings?.linkedin_url)
    },
    { 
      url: settings?.instagram_url, 
      icon: Instagram, 
      label: 'Instagram',
      isValid: isValidUrl(settings?.instagram_url)
    },
    { 
      url: settings?.contact_email ? `mailto:${settings.contact_email}` : null, 
      icon: Mail, 
      label: 'Email',
      isValid: settings?.contact_email && settings.contact_email.trim().length > 0
    },
  ].filter(link => link.isValid);

  return (
    <footer className="bg-slate-900 text-slate-400" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-2 md:grid-cols-5 gap-8">
          {/* Brand Column */}
          <div className="col-span-2 md:col-span-1">
            <Link to="/" className="flex items-center gap-2 mb-4">
              {settings?.logo_url ? (
                <img src={settings.logo_url} alt={settings?.company_name || 'MoveAbroadIQ'} className="h-8" />
              ) : (
                <>
                  <div className="w-8 h-8 bg-white rounded-lg flex items-center justify-center">
                    <Globe className="w-5 h-5 text-slate-900" />
                  </div>
                  <span className="font-bold text-lg text-white">
                    {settings?.website_name || 'MoveAbroadIQ'}
                  </span>
                </>
              )}
            </Link>
            <p className="text-sm text-slate-500 mb-4">
              {settings?.company_description || 'Data-driven relocation intelligence for digital nomads and expats.'}
            </p>
            <div className="flex gap-3">
              {socialLinks.length > 0 ? (
                socialLinks.map(({ url, icon: Icon, label }) => (
                  <a 
                    key={label}
                    href={url} 
                    target={label === 'Email' ? '_self' : '_blank'}
                    rel={label === 'Email' ? '' : 'noopener noreferrer'}
                    className="p-2 bg-slate-800 rounded-lg hover:bg-slate-700 transition-colors"
                    aria-label={label}
                    data-testid={`social-${label.toLowerCase()}`}
                  >
                    <Icon className="w-4 h-4" />
                  </a>
                ))
              ) : (
                /* No social icons shown if none configured in admin */
                <p className="text-xs text-slate-600">Social links coming soon</p>
              )}
            </div>
          </div>

          {/* Link Columns */}
          {Object.entries(footerLinks).map(([title, links]) => (
            <div key={title}>
              <h4 className="text-white font-semibold mb-4 text-sm">{title}</h4>
              <ul className="space-y-2">
                {links.map((link) => (
                  <li key={link.name}>
                    <Link
                      to={link.href}
                      className="text-sm text-slate-500 hover:text-white transition-colors"
                    >
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-slate-800">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-sm text-slate-500">
              © {currentYear} MoveAbroadIQ. All rights reserved.
            </p>
            <div className="flex gap-6">
              <Link to="/terms" className="text-sm text-slate-500 hover:text-white">Terms</Link>
              <Link to="/privacy" className="text-sm text-slate-500 hover:text-white">Privacy</Link>
              <Link to="/cookies" className="text-sm text-slate-500 hover:text-white">Cookies</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};
